import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Check, X, Crown, Building2, Zap, FileText, Award, 
  Shield, HelpCircle, Star, Pause, Calculator
} from 'lucide-react';
import { BUSINESS_CONFIG } from '../config/businessConfig';

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'

  // Scroll to comparison section if hash is present
  useEffect(() => {
    if (location.hash === '#comparison') {
      setTimeout(() => {
        const element = document.getElementById('comparison');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  const pricingTiers = [
    {
      name: "Demo Sandbox",
      price: "$0",
      monthlyPrice: "$0",
      annualPrice: "$0",
      period: "",
      description: "Try before you buy",
      highlight: false,
      tier: 'demo',
      features: [
        { name: "Emissions Entries", value: "5 total", included: true },
        { name: "NGA Factors", value: "Auto-applied yearly", included: true },
        { name: "Storage", value: "None", included: "partial" },
        { name: "Attachments", value: "Not available", included: false },
        { name: "OCR Auto-Extract", value: "Not available", included: false },
        { name: "Reports", value: "Watermarked 'Sample'", included: "partial" },
        { name: "Support", value: "Self-service", included: "partial" },
      ],
      cta: "Start Free Demo",
      ctaAction: () => navigate('/signup?plan=demo')
    },
    {
      name: "Standard",
      monthlyPrice: "$11",
      annualPrice: "$118.80",
      annualSavings: "Save $13.20/yr",
      description: "Perfect for small businesses",
      highlight: false,
      tier: 'standard',
      features: [
        { name: "Emissions Entries", value: "10,000", included: true },
        { name: "NGA Factors", value: "Auto-applied yearly", included: true },
        { name: "Storage", value: "1GB Total", included: true },
        { name: "Attachments", value: "File & Photo Uploads", included: true },
        { name: "Reports", value: "Single-click PDF Export", included: true },
        { name: "OCR Auto-Extract", value: "Pro only", included: false },
        { name: "Support", value: "Standard Email", included: true },
      ],
      cta: "Get Standard",
      ctaAction: () => navigate('/signup?plan=standard')
    },
    {
      name: "Pro",
      monthlyPrice: "$22",
      annualPrice: "$237.60",
      annualSavings: "Save $26.40/yr",
      description: "Full compliance suite",
      highlight: true,
      badge: "Most Popular",
      tier: 'pro',
      features: [
        { name: "Emissions Entries", value: "100,000", included: true },
        { name: "NGA Factors", value: "Auto-applied yearly", included: true },
        { name: "Storage", value: "10GB Evidence Vault", included: true },
        { name: "Attachments", value: "File & Photo Uploads", included: true },
        { name: "OCR Auto-Extract", value: "Auto-detect emissions data", included: true },
        { name: "Reports", value: "Full Templates + AI Co-Pilot", included: true },
        { name: "Agent Co-Pilot", value: "Ask questions anytime", included: true },
        { name: "Support", value: "Priority Email", included: true },
      ],
      overageNote: "Extra storage: $2/GB/mo • Extra rows: $1/10k/mo",
      cta: "Get Pro",
      ctaAction: () => navigate('/signup?plan=pro')
    }
  ];

  // Multi-org add-on discount
  const addonDiscount = 20;
  const addonTiers = [
    {
      name: "Standard Add-on",
      description: "Additional organisation",
      monthlyPrice: "$8.80",
      annualPrice: "$95.04",
      originalMonthly: "$11",
      originalAnnual: "$118.80",
      savings: "20% multi-org discount",
      tier: 'standard_addon'
    },
    {
      name: "Pro Add-on",
      description: "Additional organisation",
      monthlyPrice: "$17.60",
      annualPrice: "$190.08",
      originalMonthly: "$22",
      originalAnnual: "$237.60",
      savings: "20% multi-org discount",
      tier: 'pro_addon'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-[#74A57F]/10 text-[#74A57F] text-sm font-medium rounded-full mb-6">
              Simple, Transparent Pricing
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose Your Path to<br />
              <span className="text-[#74A57F]">Carbon Compliance</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Whether you're a sole trader or managing multiple entities across Australia, 
              we've got a plan that fits. All prices include GST.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-white text-[#003B5C] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'annual' 
                    ? 'bg-white text-[#003B5C] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Annual
                <span className="bg-[#74A57F] text-white text-xs px-2 py-0.5 rounded-full">
                  Save 10%
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  tier.highlight 
                    ? 'bg-[#003B5C] text-white shadow-2xl scale-105 border-2 border-[#74A57F]' 
                    : 'bg-white border border-slate-200 shadow-lg'
                }`}
                data-testid={`pricing-tier-${tier.tier}`}
              >
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex items-center px-4 py-1 bg-[#74A57F] text-white text-sm font-semibold rounded-full shadow-lg">
                      <Star className="w-4 h-4 mr-1" />
                      {tier.badge}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className={`text-xl font-bold mb-2 ${tier.highlight ? 'text-white' : 'text-[#003B5C]'}`}>
                    {tier.name}
                  </h3>
                  <p className={`text-sm mb-4 ${tier.highlight ? 'text-white/70' : 'text-slate-500'}`}>
                    {tier.description}
                  </p>
                  <div className="flex items-baseline justify-center">
                    <span className={`text-5xl font-bold ${tier.highlight ? 'text-white' : 'text-[#003B5C]'}`}>
                      {billingCycle === 'annual' ? tier.annualPrice : tier.monthlyPrice}
                    </span>
                    {tier.monthlyPrice !== "$0" && (
                      <span className={`text-lg ml-1 ${tier.highlight ? 'text-white/70' : 'text-slate-500'}`}>
                        {billingCycle === 'annual' ? '/year' : '/month'}
                      </span>
                    )}
                  </div>
                  {billingCycle === 'annual' && tier.annualSavings && (
                    <p className="text-[#74A57F] text-sm mt-2 font-medium">{tier.annualSavings}</p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      {feature.included === true ? (
                        <Check className={`w-5 h-5 mr-3 flex-shrink-0 ${tier.highlight ? 'text-[#74A57F]' : 'text-[#74A57F]'}`} />
                      ) : feature.included === "partial" ? (
                        <div className={`w-5 h-5 mr-3 flex-shrink-0 rounded-full border-2 ${tier.highlight ? 'border-white/40' : 'border-slate-300'}`} />
                      ) : (
                        <X className={`w-5 h-5 mr-3 flex-shrink-0 ${tier.highlight ? 'text-white/40' : 'text-slate-300'}`} />
                      )}
                      <div>
                        <span className={`text-sm font-medium ${tier.highlight ? 'text-white/90' : 'text-slate-700'}`}>
                          {feature.name}
                        </span>
                        <span className={`text-sm block ${tier.highlight ? 'text-white/60' : 'text-slate-500'}`}>
                          {feature.value}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>

                {tier.overageNote && (
                  <p className={`text-xs mb-4 text-center ${tier.highlight ? 'text-white/60' : 'text-slate-400'}`}>
                    {tier.overageNote}
                  </p>
                )}

                <Button
                  onClick={tier.ctaAction}
                  className={`w-full py-6 text-base font-semibold ${
                    tier.highlight
                      ? 'bg-[#74A57F] hover:bg-[#5F8575] text-white'
                      : 'bg-[#003B5C] hover:bg-[#002A47] text-white'
                  }`}
                  data-testid={`pricing-cta-${tier.tier}`}
                >
                  {tier.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Org Add-on Section */}
      <section className="py-12 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Building2 className="w-4 h-4" />
              Multi-Organisation Discount
            </div>
            <h2 className="text-2xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Managing Multiple Businesses?
            </h2>
            <p className="text-slate-600 mt-2">
              Add unlimited organisations to your account with a <strong>20% discount</strong> on each add-on.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {addonTiers.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-[#003B5C]">{addon.name}</h3>
                    <p className="text-sm text-slate-500">{addon.description}</p>
                  </div>
                  <span className="bg-[#74A57F]/10 text-[#74A57F] text-xs font-semibold px-3 py-1 rounded-full">
                    {addon.savings}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#003B5C]">
                    {billingCycle === 'annual' ? addon.annualPrice : addon.monthlyPrice}
                  </span>
                  <span className="text-sm text-slate-500">
                    {billingCycle === 'annual' ? '/year' : '/month'}
                  </span>
                  <span className="text-sm text-slate-400 line-through">
                    {billingCycle === 'annual' ? addon.originalAnnual : addon.originalMonthly}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            * Add-on orgs get 20% off. Annual billing saves an additional 10% (discounts stack!).
          </p>
        </div>
      </section>

      {/* Account Freeze Option */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-2xl p-8 border border-slate-200"
          >
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Pause className="w-7 h-7 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Account Freeze Option
                </h3>
                <p className="text-slate-600 mb-4">
                  Need to pause your subscription? Freeze your account for just <strong>$4/month</strong> and 
                  we'll securely retain all your data and attachments. Resume anytime.
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-slate-500">
                    <Check className="w-4 h-4 text-[#74A57F]" /> Data retained
                  </span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <Check className="w-4 h-4 text-[#74A57F]" /> Resume anytime
                  </span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <Check className="w-4 h-4 text-[#74A57F]" /> No data loss
                  </span>
                  <span className="flex items-center gap-1 text-amber-600 text-xs font-medium">
                    * Storage must be under 10GB
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-[#003B5C]">$4</span>
                <span className="text-slate-500">/month</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section id="comparison" className="py-16 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-[#003B5C] text-center mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Feature Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden" data-testid="pricing-comparison-table">
              <thead>
                <tr className="bg-[#003B5C] text-white">
                  <th className="text-left py-4 px-6 font-semibold">Feature</th>
                  <th className="text-center py-4 px-6 font-semibold">Demo</th>
                  <th className="text-center py-4 px-6 font-semibold">Standard</th>
                  <th className="text-center py-4 px-6 font-semibold bg-[#74A57F]">Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Monthly Price</td>
                  <td className="py-4 px-6 text-center text-slate-600">$0</td>
                  <td className="py-4 px-6 text-center text-slate-600">$11/mo</td>
                  <td className="py-4 px-6 text-center text-slate-600 bg-[#74A57F]/5 font-semibold">$22/mo</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Annual Price</td>
                  <td className="py-4 px-6 text-center text-slate-600">$0</td>
                  <td className="py-4 px-6 text-center text-slate-600">$118.80/yr</td>
                  <td className="py-4 px-6 text-center text-slate-600 bg-[#74A57F]/5 font-semibold">$237.60/yr</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Emissions Entries</td>
                  <td className="py-4 px-6 text-center text-slate-600">5 total</td>
                  <td className="py-4 px-6 text-center text-slate-600">10,000</td>
                  <td className="py-4 px-6 text-center text-slate-600 bg-[#74A57F]/5 font-semibold">100,000</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">NGA Factors</td>
                  <td className="py-4 px-6 text-center text-slate-600">Auto-applied</td>
                  <td className="py-4 px-6 text-center text-slate-600">Auto-applied</td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5">
                    <span className="text-slate-600">Auto-applied</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Evidence Vault (Storage)</td>
                  <td className="py-4 px-6 text-center text-slate-500">None</td>
                  <td className="py-4 px-6 text-center text-slate-600">1GB</td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5">
                    <span className="text-[#74A57F] font-semibold">10GB</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Attachments (File/Photo)</td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><Check className="w-5 h-5 text-[#74A57F] mx-auto" /></td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5"><Check className="w-5 h-5 text-[#74A57F] mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">OCR Auto-Extract</td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5"><Check className="w-5 h-5 text-[#74A57F] mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Reports</td>
                  <td className="py-4 px-6 text-center text-slate-500">Watermarked</td>
                  <td className="py-4 px-6 text-center text-slate-600">Single-click PDF</td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5">
                    <span className="text-[#74A57F] font-semibold">Full Templates</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">AI Co-Pilot (Narratives)</td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5"><Check className="w-5 h-5 text-[#74A57F] mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Agent Co-Pilot (Q&A)</td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5"><Check className="w-5 h-5 text-[#74A57F] mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-slate-700 font-medium">Support</td>
                  <td className="py-4 px-6 text-center text-slate-500">Self-service</td>
                  <td className="py-4 px-6 text-center text-slate-600">Standard Email</td>
                  <td className="py-4 px-6 text-center bg-[#74A57F]/5">
                    <span className="text-[#74A57F] font-semibold">Priority Email</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Link Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#003B5C]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-[#003B5C]" />
          </div>
          <h2 className="text-2xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Got Questions?
          </h2>
          <p className="text-slate-600 mb-6">
            Find answers to common questions about carbon compliance, NGA factors, and sustainability reporting.
          </p>
          <Button
            onClick={() => navigate('/faq')}
            variant="outline"
            className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white px-8 py-6 text-lg"
          >
            View All FAQs
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#003B5C] to-[#005580]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Start Your Compliance Journey?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Join hundreds of Australian SMEs already using {BUSINESS_CONFIG.name} to track emissions, 
            access grants, and meet 2026 compliance standards.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/signup')}
              className="bg-[#74A57F] hover:bg-[#5F8575] text-white px-8 py-6 text-lg"
              data-testid="pricing-final-cta"
            >
              Start Your Free Demo
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
